import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import CallAPI from "../../services/apiCalls";
import useUserStore from "../../utils/storeApp";
import { ProgressBar } from "primereact/progressbar";
import { usePortalAuthentication } from "../../services/authorizationService";

const passwordValidatorConstants = {
  digits: /\d+/,
  letters: /[a-zA-Z]+/,
  uppercase: /[A-Z]+/,
  lowercase: /[a-z]+/,
  symbols: /[`~\!@#\$%\^\&\*\(\)\-_\=\+\[\{\}\]\\\|;:'",<.>\/\?€£¥₹]+/,
  spaces: /[\s]+/,
  repeat: /(.+)\1{1,}/, //  /(.+).*(\1.*){1,}/  ,///(.+)\1{1,}/
  seq: /(ab|bc|cd|de|ef|fg|gh|hi|ij|jk|kl|lm|mn|no|op|pq|qr|rs|st|tu|uv|vw|wx|xy|yz|01|12|23|34|45|56|67|78|89)+/,
};

const BodyPassword = () => {
  const { userToken, userSession, SetSharedObject, sharedObject } =
    useUserStore((state) => ({
      userToken: state.userToken,
      userSession: state.userSession,
      SetSharedObject: state.SetSharedObject,
      sharedObject: state.sharedObject,
    }));

  const { login } = usePortalAuthentication();
  const navigate = useNavigate();
  const [currentPasswordFlag, SetCurrentPasswordFlag] = useState(true);
  const [PasswordMatchFlag, SetPasswordMatchFlag] = useState(false);
  const [errFlag, setErrFlag] = useState(false);
  const [userName, setUserName] = useState(userSession.preferred_username ?? '');
  const [isUsernameInvalid, setIsUsernameInvalid] = useState(false);
  const [errMsg, setErrMsg] = useState(
    "System encountered an error. Please try again or contact administrator."
  );
  const [userCode, setUserCode] = useState();
  const [firstName, setFirstName] = useState(userSession.given_name);
  const [lastName, setLastName] = useState(userSession.family_name);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [successFlag, setSuccessFlag] = useState(false);
  const [resetFlag, setResetFlag] = useState(false);
  const [processing, SetProcesing] = useState(false);
  const [meter, SetMeter] = useState("");
  const [showCurrentPassword, SetShowCurrentPassword] = useState(false);
  const [showNewPassword, SetShowNewPassword] = useState(false);
  const [showConfirmPassword, SetShowConfirmPassword] = useState(false);
  const [submitFlag, SetSubmitFlag] = useState(false);
  const [answers, setAnswers] = useState({
    CurrentPassword: "",
    NewPassword: "",
    NewPasswordRepeat: "",
  });
  const setAnswer = async (questionId, answer) => {
    await setAnswers({ ...answers, [questionId]: answer });
  };

  useEffect(() => {
    setResetFlag(false);

    if (sharedObject.ResetPasswordObj?.UserId && sharedObject.ResetPasswordObj?.Code) {
      setIsResettingPassword(true);
      setUserCode(sharedObject.ResetPasswordObj?.Code);
    }
    else {
      setIsResettingPassword(false);
    }

    return () => {
      SetSharedObject("changePasswordFlag", false);
    };
  }, []);

  const submitChangePassword = async () => {
    SetSubmitFlag(true);
    SetPasswordMatchFlag(false);
    setResetFlag(false);
    setSuccessFlag(false);
    setErrFlag(false);

    if (isResettingPassword && !userName) {
      setIsUsernameInvalid(true);
      document.getElementById("Username").scrollIntoView();
      document.getElementById("Username").focus();
      return;
    } else if (answers.CurrentPassword === "" && !isResettingPassword) {
      document.getElementById("CurrentPassword").scrollIntoView();
      document.getElementById("CurrentPassword").focus();
      return;
    } else if (answers?.NewPassword === "") {
      document.getElementById("NewPassword").scrollIntoView();
      document.getElementById("NewPassword").focus();
      return;
    } else if (answers?.NewPasswordRepeat === "") {
      document.getElementById("NewPasswordRepeat").scrollIntoView();
      document.getElementById("NewPasswordRepeat").focus();
      return;
    }

    if (answers.NewPassword !== answers.NewPasswordRepeat) {
      SetPasswordMatchFlag(true);
      return;
    }

    let call = "";
    let mdl = {};
    if (!isResettingPassword) {
      if (sharedObject.ResetPasswordId) {
        call = "/admin/UserchangePassword";
      } else {
        call = "/admin/ChangePassword";
      }
      mdl = {
        OldPassword: answers.CurrentPassword,
        NewPassword: answers.NewPassword,
        ConfirmPassword: answers.NewPasswordRepeat,
      };
    } else {
      call = "/admin/PasswordReset";
      mdl = {
        Code: userCode,
        Password: answers.NewPassword,
        ConfirmPassword: answers.NewPasswordRepeat,
        userName: userName,
      };
    }
    SetProcesing(true);

    const res = await CallAPI(call, mdl, "post", userToken);
    SetSharedObject("ResponseCode", res.code);

    if (res && res.status === "SUCCESS") {
      if (!isResettingPassword) {
        if (sharedObject.changePasswordFlag) {
          setSuccessFlag(true);
        } else {
          if (res.data.page === "Register") {
            navigate("/pre-registration");
          } else if (res.data.page === "Dashboard") {
            navigate("/dashboard");
          } else if (res.data.page === "SSO") {
            navigate("/experian-sso");
          }
        }
      } else {
        setResetFlag(true);
      }

      SetSharedObject("ResetPasswordId", null);
      SetProcesing(false);

    } else if (res.status === "SERVERERROR") {
      if (
        res?.data &&
        Object.keys(res?.data).length > 0 &&
        res?.data[0]?.code === "PasswordMismatch"
      ) {
        setErrMsg("The current password you entered does not match.");
      } else if (res?.data?.code === "UsernameNotFound") {
        setErrMsg("Username does not match");
      } else if (res.data &&
        res.data.includes(
          "New password cannot be the same as the old password."
        )
      ) {
        setErrMsg("New password cannot be the same as the old password.");
      }
      setErrFlag(true);
      SetProcesing(false);
    }
  };

  const ValidatePassword = async (val) => {
    SetCurrentPasswordFlag(true);
    let flag = true;
    if (val.length < 8 || val.length > 256) {
      flag = false;
      SetCurrentPasswordFlag(true);
      SetMeter(0);
    } else if (val.length >= 8 && val.length <= 256) {
      if (_process(passwordValidatorConstants.seq, val)) {
        SetCurrentPasswordFlag(true);
        SetMeter(0);
      } else {
        const numberRegex = /\d/;
        const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;
        let nr = numberRegex.test(val);
        let specialChar = specialCharRegex.test(val);
        flag = nr && specialChar;
        if (flag) {
          SetMeter(2);
        } else {
          SetMeter(1);
        }
        SetCurrentPasswordFlag(false);
      }
    }
    await setAnswer("NewPassword", val);
  };

  const _process = (regexp, password) => {
    let pw = password.toLowerCase();
    let uname = userName ? userName.toLowerCase() : "";
    let fname = firstName ? firstName.toLowerCase() : "";
    let lname = lastName ? lastName.toLowerCase() : "";
    let check = false;
    if (new RegExp(regexp).test(password)) {
      check = true;
    } else if (uname && pw.includes(uname) || (!isResettingPassword && (pw.includes(fname) || pw.includes(lname)))) {
      check = true;
    }
    return check;
  };

  return (
    <>
      {resetFlag ? (
        <section className="layout-container">
          <div className="container ctr">
            <div className="bi-success-check">
              <i className="bi bi-check2-circle" alt="Success Checkmark"></i>
            </div>
            <div className="lg-msg">
              Password for {userName} has been changed successfully.
            </div>
            <Link
              onClick={(e) => login()}
              className="btn btn-primary"
              role="button"
              aria-Labelledby="Sign In"
            >
              Sign In
            </Link>
          </div>
        </section>
      ) : successFlag ? (
        <section className="layout-container">
          <div className="container ctr">
            <div className="bi-success-check">
              <i className="bi bi-check2-circle" alt="Success Checkmark"></i>
            </div>
            <div className="lg-msg">
              Password for {firstName} {lastName} has been changed successfully.
            </div>
            <div className="descript-container">
              Your new password will take effect after you log out.
            </div>
          </div>
        </section>
      ) : (
        <div className="BodyPassword">
          <div className="left-panel">
            <form className="ch-pswd">
              <div className="field-wrapper">
                <div className="descript-container">
                  To conform with our Strong Password Policy, you can use
                  passphrase with spaces or meet the required criteria to the
                  right.
                </div>
              </div>
              <div className="field-wrapper">
                <label for="Username">Username</label>
                <input
                  type="text"
                  value={isResettingPassword ? userName : userName?.split("|")[0]}
                  className="form-control"
                  aria-describedby="Enter Username"
                  id="Username"
                  disabled={isResettingPassword ? false : true}
                  onChange={(el) => {
                    setUserName(el.target.value);
                    setIsUsernameInvalid(false);
                  }}
                />
                {isUsernameInvalid && submitFlag ? (
                  <div className="alert alert-danger" role="alert">
                    Username is required.
                  </div>
                ) : null}
              </div>

              {/* Current Password */}
              {/*Hide current password when resetting password.*/}
              {!isResettingPassword ? (
                <div className="field-wrapper">
                  <label for="CurrentPassword">Current Password</label>
                  <div className="input-group mb-3">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      className="form-control password-input"
                      aria-describedby="Enter Current Password"
                      id="CurrentPassword"
                      value={answers.CurrentPassword}
                      onChange={(el) =>
                        setAnswer("CurrentPassword", el.target.value)
                      }
                    />
                    <div className="input-group-append">
                      <button
                        className="input-group-text btn btn-secondary"
                        id="show-current-password-btn"
                        type="button"
                        onClick={(e) => SetShowCurrentPassword(!showCurrentPassword)}
                      >
                        <i
                          className={`bi ${showCurrentPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                            }`}
                          alt="Hide Password Button"
                          title="Hide Password Eye Icon"
                        ></i>
                      </button>
                    </div>
                  </div>
                  {currentPasswordFlag && submitFlag ? (
                    <div className="alert alert-danger" role="alert">
                      Current password does not match.
                    </div>
                  ) : null}
                </div>
              ) : null}

              {/* New Password */}
              <div className="field-wrapper">
                <label for="NewPassword">New Password</label>
                <div className="input-group mb-3">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control password-input"
                    autocomplete="new-password"
                    size="35"
                    id="NewPassword"
                    value={answers.NewPassword}
                    onChange={(el) => ValidatePassword(el.target.value)}
                  />
                  <div className="input-group-append">
                    <button
                      className="input-group-text btn btn-secondary"
                      id="show-new-password-btn"
                      type="button"
                      onClick={(e) => SetShowNewPassword(!showNewPassword)}
                    >
                      <i
                        className={`bi ${showNewPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                          }`}
                        alt="Hide Password Button"
                        title="Hide Password Eye Icon"
                      ></i>
                    </button>
                  </div>
                </div>
              </div>
              {/* Password Strength */}
              <div className="field-wrapper">
                <label for="password-strength-meter">Password Strength</label>
                <div className="input-group">
                  {/* <meter
                className={`password-strength-container form-control  `}
                id="password-strength-meter"
              ></meter> */}
                </div>
                <div className="card">
                  <ProgressBar
                    value={
                      meter === 0
                        ? 25
                        : meter === 1
                          ? 50
                          : meter === 2
                            ? 100
                            : 0
                    }
                  ></ProgressBar>
                </div>
                {meter === 0 ? (
                  <aside className="pswd-strength-weak">
                    <label className="rd-clr">Weak</label>
                    <ul>
                      <li>
                        Add another word or two. Uncommon words are better.
                      </li>
                      <li>Password length cannot be less than 8 characters.</li>
                      <li>Password is very weak and can be hacked easily.</li>
                      <li>Password must not contain repeated characters.</li>
                      <li>Password must not contain sequential characters.</li>
                    </ul>
                  </aside>
                ) : meter === 1 ? (
                  <aside className="pswd-strength-good">
                    <label className="or-clr">Good</label>
                    <ul>
                      <li>Password is good but still can be hacked.</li>
                      <li>
                        Password is acceptable but may need to be changed more
                        frequently.
                      </li>
                    </ul>
                  </aside>
                ) : meter === 2 ? (
                  <aside>
                    <label className="g-clr">Strong</label>
                  </aside>
                ) : null}
              </div>
              {/* Confirm Password */}
              <div className="field-wrapper">
                <label for="NewPasswordRepeat">Confirm Password</label>
                <div className="input-group mb-3">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control password-input"
                    autocomplete="new-password"
                    aria-label="Enter username"
                    aria-describedby="basic-addon2"
                    id="NewPasswordRepeat"
                    value={answers.NewPasswordRepeat}
                    onChange={(el) =>
                      setAnswer("NewPasswordRepeat", el.target.value)
                    }
                  />
                  <div className="input-group-append">
                    <button
                      className="input-group-text btn btn-secondary"
                      id="show-confirm-password-btn"
                      type="button"
                      onClick={(e) => SetShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i
                        className={`bi ${showConfirmPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                          }`}
                        alt="Hide Password Button"
                        title="Hide Password Eye Icon"
                      ></i>
                    </button>
                  </div>
                </div>
                {PasswordMatchFlag &&
                  submitFlag &&
                  answers.NewPasswordRepeat !== "" ? (
                  <div className="alert alert-danger" role="alert">
                    Confirmation password does not match
                  </div>
                ) : null}
              </div>
              {errFlag ? (
                <div className="alert alert-danger" role="alert">
                  {errMsg}
                </div>
              ) : null}
              {/*After you update, should be the Experian page then back to the login*/}
              <div className="button-container">
                <button
                  to={"/pre-registration"}
                  onClick={(e) => submitChangePassword()}
                  type="button"
                  className="btn btn-success"
                  aria-label="Update Password Change"
                  aria-describedby="basic-addon2"
                  disabled={currentPasswordFlag}
                >
                  Update
                </button>
              </div>
            </form>
          </div>

          <div className="right-panel">
            <div className="icon-container">
              <i className="bi bi-shield-check"></i>
            </div>
            <div className="header-banner">Password Criteria</div>
            <ul>
              <li>
                Must be at least 8 characters in length and can be up to 256
                characters in length.
              </li>
              <li>
                Must not have repetitive or sequential characters (e.g., aa,
                abc123, 1111, 2323, 2000, xyz, abab, or 123), dictionary words,
                old passwords.
              </li>
              <li>
                Must not use common passwords like name, birthdays, usernames,
                etc.
              </li>
            </ul>
          </div>
        </div>
      )}
      {processing ? <div className="loading"></div> : null}
    </>
  );
};

export default BodyPassword;
